import { ArrowRightLeft } from "lucide-react";
import { useNavigate } from "react-router";

import { isIngestionAction } from "@/shared/domain/actions";
import { useAction } from "@/shared/domain/actions/hooks";
import {
  firesOnFileArrival,
  rulePrefillFromTrigger,
  type IngestionRulePrefill,
} from "@/shared/domain/ingestionRules";
import {
  isManaged,
  type InvokeActionTarget,
  type TriggerV2Record,
} from "@/shared/domain/triggers";
import { Callout, RobotoButton } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./ConvertToIngestionRuleCallout.module.css";

/**
 * Beside a trigger that ingests uploaded files: suggests an ingestion rule
 * instead, and opens the new-rule form filled in from the trigger. Never
 * blocks the trigger itself.
 */
export function ConvertToIngestionRuleCallout({
  prefill,
}: {
  /** Read when the button is pressed, so a form's latest values are used. */
  prefill: () => IngestionRulePrefill;
}) {
  const navigate = useNavigate();
  return (
    <Callout
      className={styles.nudge}
      data-testid="convert-to-ingestion-rule"
      action={
        <RobotoButton
          variant="secondary"
          size="sm"
          icon={<ArrowRightLeft aria-hidden="true" />}
          onClick={() =>
            void navigate(Goto.createNewIngestionRuleRoute(prefill()))
          }
          data-testid="convert-to-ingestion-rule-button"
        >
          Convert to ingestion rule
        </RobotoButton>
      }
    >
      Ingesting files? Use an ingestion rule instead: sessions then wait for
      these files, and files still waiting on ingestion are easy to find.
    </Callout>
  );
}

/**
 * The callout on a stored trigger's own page, for a target that runs an
 * ingestion action when files arrive. Never shown on a managed trigger.
 */
export function TriggerTargetIngestionNudge({
  trigger,
  target,
}: {
  trigger: TriggerV2Record;
  target: InvokeActionTarget;
}) {
  const offered = !isManaged(trigger) && firesOnFileArrival(trigger.fires_on);
  const action = useAction(
    offered ? target.action.name : undefined,
    target.action.digest ?? undefined,
    // An action stored without an owner belongs to the trigger's org.
    { resourceOwnerId: target.action.owner ?? trigger.org_id },
  );
  if (!offered || !isIngestionAction(action.data ?? target.action)) {
    return null;
  }
  return (
    <ConvertToIngestionRuleCallout
      prefill={() => rulePrefillFromTrigger(trigger, target)}
    />
  );
}
